import { faCheck, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import PropertyGroupForm, {
  PropertyGroupFormValidator
} from '../PropertyGroupForm';

function Details(props) {
  const [editingDetails, setEditingDetails] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // Options to customise UI.
  const customOptions = props?.customOptions;
  const customFormOptions = customOptions?.form;

  const restore = async () => {
    setRestoring(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_PROPERTIES_API_URL +
          'api/property-groups/' +
          props.propertyGroup.id +
          '/restore',
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + props.bearerToken,
            SeasonalDatabase: props.selectedDatabase
          }
        }
      );
      if (!response.ok) {
        if (process.env.NODE_ENV === 'development') console.error(err);
        if (res.status === 401) props.refreshBearerToken();
        else throw Error(res.statusText);
      } else {
        const data = await response.json();
        if (data) props.update(data);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setRestoring(false);
    }
  };

  return props.propertyGroup.userProperty.can_edit && editingDetails ? (
    <Formik
      initialValues={{
        name: props.propertyGroup.name,
        group_type: props.propertyGroup.group_type,
        description: props.propertyGroup.description || ''
      }}
      validate={PropertyGroupFormValidator}
      onSubmit={(values, actions) => {
        fetch(
          `${process.env.REACT_APP_PROPERTIES_API_URL}api/property-groups/${props.propertyGroup.id}?with[]=properties&with[]=users.propertyRole&append[]=userProperty`,
          {
            method: 'PUT',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${props.bearerToken}`,
              SeasonalDatabase: props.selectedDatabase
            },
            body: JSON.stringify(values)
          }
        )
          .then((res) => {
            if (res.ok)
              res.json().then((data) => {
                if (data) {
                  props.update(data);
                  setEditingDetails(false);
                }
              });
            if (res.status === 422)
              res.json().then((data) => {
                if (data.errors) {
                  Object.keys(data.errors).forEach((key) => {
                    actions.setFieldError(key, data.errors[key][0]);
                  });

                  actions.setSubmitting(false);
                }
              });
            else if (res.status === 401) props.refreshBearerToken();
            else throw Error(res.statusText);
          })
          .catch((err) => {
            if (process.env.NODE_ENV === 'development') console.error(err);
            actions.setSubmitting(false);
          });
      }}
    >
      {(props) => (
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            Details
            <div>
              <Button
                variant='link'
                className='text-success'
                type='button'
                onClick={props.submitForm}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button
                variant='link'
                className='text-danger'
                type='button'
                onClick={() => setEditingDetails(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </div>
          <div className='card-body'>
            <PropertyGroupForm {...props} customOptions={customFormOptions} />
          </div>
        </div>
      )}
    </Formik>
  ) : (
    <Card>
      <Card.Header className='d-flex justify-content-between align-items-center'>
        Details
        {props.propertyGroup.userProperty.can_edit ? (
          !props.propertyGroup.deleted_at ? (
            /* Edit button */
            <Button
              variant='link'
              type='button'
              onClick={() => setEditingDetails(true)}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
          ) : (
            /* Restore button */
            <Button
              variant='outline-danger'
              type='button'
              disabled={restoring}
              onClick={async () => {
                await restore();
              }}
            >
              {restoring ? (
                <Spinner
                  className='mx-3'
                  animation='border'
                  variant='danger'
                  size='sm'
                />
              ) : (
                'Restore'
              )}
            </Button>
          )
        ) : null}
      </Card.Header>
      <Card.Body>
        <Fragment>
          <Row>
            <Col>Name</Col>
            <Col>{props.propertyGroup.name}</Col>
          </Row>
          <Row>
            <Col>Description</Col>
            <Col>{props.propertyGroup.description}</Col>
          </Row>
        </Fragment>
      </Card.Body>
    </Card>
  );
}

Details.propTypes = {
  // Authentication
  bearerToken: PropTypes.string.isRequired,
  selectedDatabase: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,

  // Property Group
  propertyGroup: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,

  // Custom Options
  customOptions: PropTypes.object
};

export default Details;
